// Iter 125 — permissions context.
// Single fetch on login; exposes effective hub + destination sets to the
// whole React tree so the SectionNav, Hub pages, and ⌘K palette can filter.
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PermissionsContext = createContext({
  loaded: false,
  hubs: new Set(),
  destinations: new Set(),
  canHub: () => true,
  canDestination: () => true,
  refresh: () => Promise.resolve(),
});

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [hubs, setHubs] = useState(new Set());
  const [destinations, setDestinations] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user) {
      setHubs(new Set()); setDestinations(new Set()); setLoaded(true); return;
    }
    try {
      const r = await axios.get(`${API}/permissions/me`, { withCredentials: true });
      setHubs(new Set(r.data.hubs || []));
      setDestinations(new Set(r.data.destinations || []));
    } catch {
      // Fail OPEN so a broken endpoint doesn't lock people out — the backend
      // still 403s on actual data fetches when 4b enforcement is in place.
      setHubs(new Set(['dashboard']));
      setDestinations(new Set());
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = {
    loaded,
    hubs,
    destinations,
    canHub: (id) => hubs.has(id),
    canDestination: (id) => destinations.has(id),
    refresh,
  };
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export const usePermissions = () => useContext(PermissionsContext);
